import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    Data,
    ParamMap,
    ResolveFn,
    Route,
} from '@angular/router';
import {inject} from '@angular/core';
import {selectBuilding, selectLocation, selectMeter, selectUser} from 'src/app/state/selectors';
import {Store} from '@ngrx/store';
import {combineLatest, filter, map, Observable, of, switchMap, take, tap} from 'rxjs';
import {loadBuilding, loadLocation, loadMeter, loadUser} from 'src/app/state/actions';
import {
    BuildingStateSuccess,
    LocationStateSuccess,
    MeterStateSuccess,
    StateError,
    UserStateSuccess,
} from 'src/app/state/reducers';
import {BuildingServices, FeatureFlagService, TranslateService} from '@brunata-angular/util';
import {catchError} from 'rxjs/operators';
import {UserAdministrationService} from '@shared/services/user-administration.service';
import {z} from 'zod';

export const systemInformationLink = (buildingNo: string): string[] => [
    '/admin',
    'system',
    buildingNo,
    'information',
];

export const buildingResolver: ResolveFn<string> = (route: ActivatedRouteSnapshot) => {
    const store = inject(Store);
    const buildingNo = route.paramMap.get('buildingno');
    if (buildingNo == null) {
        return of('no buildingno');
    }
    store.dispatch(loadBuilding({buildingNo: buildingNo}));
    return store.select(selectBuilding).pipe(
        filter((buildingState): buildingState is BuildingStateSuccess | StateError =>
            ['success', 'error'].includes(buildingState.status),
        ),
        map(buildingState =>
            buildingState.status === 'success'
                ? (buildingState.buildingDetails.building.buildingName ?? buildingState.buildingNo)
                : 'error',
        ),
    );
};

export const meterResolver: ResolveFn<string> = (route: ActivatedRouteSnapshot) => {
    const store = inject(Store);
    const meterId = route.paramMap.get('meterid');
    if (meterId == null) {
        return of('no meterid');
    }

    return store.select(selectBuilding).pipe(
        filter(
            (buildingState): buildingState is BuildingStateSuccess =>
                buildingState.status === 'success',
        ),
        tap(buildingState =>
            store.dispatch(loadMeter({buildingNo: buildingState.buildingNo, meterId: meterId})),
        ),
        switchMap(() => store.select(selectMeter)),
        filter((meterState): meterState is MeterStateSuccess | StateError =>
            ['success', 'error'].includes(meterState.status),
        ),
        map(meterState => {
            if (meterState.status === 'error') {
                return 'error';
            }
            const {meterNo, printedSerialNo} = meterState.meter;
            return `${meterNo}${printedSerialNo != null ? ` (${printedSerialNo})` : ''}`;
        }),
    );
};

export const locationResolver: ResolveFn<string> = (route: ActivatedRouteSnapshot) => {
    // TODO: Format the address from the backend
    function formatAddress(
        street: string | null,
        stairway: string | null,
        floor: string | null,
    ): string {
        return `${street ?? ''}${stairway != null ? ` ${stairway}` : ''}${floor != null ? `, ${floor}` : ''}`;
    }

    const store = inject(Store);
    const params = getMergedParams(route.root);
    const buildingNo = params.get('buildingno');
    const locationId = params.get('locationid');
    if (buildingNo == null || locationId == null) {
        throw new Error('Missing parameters');
    }
    store.dispatch(loadLocation({buildingNo: buildingNo, locationId: locationId}));
    return store.select(selectLocation).pipe(
        filter((locationState): locationState is LocationStateSuccess | StateError =>
            ['success', 'error'].includes(locationState.status),
        ),
        map(locationState =>
            locationState.status === 'success'
                ? formatAddress(
                      locationState.location.location.street,
                      locationState.location.location.stairway,
                      locationState.location.location.floor,
                  )
                : locationId,
        ),
    );
};

export const consumerResolver: ResolveFn<string> = (route: ActivatedRouteSnapshot) => {
    const store = inject(Store);
    const params = getMergedParams(route.root);
    const buildingNo = params.get('buildingno');
    const locationId = params.get('locationid');
    const consumerId = params.get('consumerid');
    if (buildingNo == null || locationId == null || consumerId == null) {
        throw new Error('Missing parameters');
    }

    // Reload on every visit: the consumer list edits consumers without touching the store, so a
    // location kept from an earlier visit can be stale.
    store.dispatch(loadLocation({buildingNo, locationId}));

    return store.select(selectLocation).pipe(
        filter((locationState): locationState is LocationStateSuccess => {
            if (locationState.status === 'error') {
                throw new Error('Error when loading location');
            }
            return locationState.status === 'success';
        }),
        map(locationState =>
            locationState.location.consumers.find(consumer => consumer.consumerId === consumerId),
        ),
        map(consumer => consumer?.name ?? consumerId),
    );
};

export const userResolver: ResolveFn<string> = (route: ActivatedRouteSnapshot) => {
    const loginNameId = route.paramMap.get('loginNameId');
    if (loginNameId == null) {
        return of('?');
    }
    return inject(UserAdministrationService)
        .getUser(Number(loginNameId))
        .pipe(
            map(user => user.personName ?? user.userName),
            catchError(() => of(loginNameId)),
        );
};

/** The text a route declares under `keyField` in its own config, not inherited from a parent. */
const getOwnTranslation = (
    route: Route | ActivatedRouteSnapshot,
    keyField: 'key' | 'titleKey',
    translateService: TranslateService,
): string | null => {
    const ownData = 'routeConfig' in route ? route.routeConfig?.data : route.data;
    const key: unknown = ownData?.[keyField];
    const module: unknown = ownData?.['module'];
    if (typeof key === 'string' && typeof module === 'string') {
        return translateService.translate(key, 'online', module);
    }
    return null;
};

export const getRouteLabel = (
    route: Route | ActivatedRouteSnapshot | null,
    translateService: TranslateService,
): string => {
    if (route == null) {
        return '?';
    }

    // Angular inherits a componentless parent's resolved data into its children, so a child's own
    // label has to beat the parent's `breadcrumb`.
    const ownLabel = getOwnTranslation(route, 'key', translateService);
    if (ownLabel != null) {
        return ownLabel;
    }

    const breadcrumb: unknown = route.data?.['breadcrumb'];
    if (typeof breadcrumb === 'string') {
        return breadcrumb;
    }

    const meterNo: unknown = route.data?.['meterNo'];
    if (typeof meterNo === 'string') {
        return `${translateService.translate('properties_meterNo', 'online', 'systems')}: ${meterNo}`;
    }

    const key: unknown = route.data?.['key'];
    const module: unknown = route.data?.['module'];
    if (typeof key === 'string' && typeof module === 'string') {
        return translateService.translate(route.data?.['key'], 'online', route.data?.['module']);
    }

    if ('path' in route) {
        return route.path ?? '?';
    }
    if ('url' in route) {
        return route.url.map(segment => segment.path).join('/');
    }
    return '?';
};

export const getRouteTitle = (
    route: ActivatedRouteSnapshot | undefined,
    translateService: TranslateService,
): string | null => (route == null ? null : getOwnTranslation(route, 'titleKey', translateService));

/** Whether a route declares a label of its own, rather than only inheriting its parent's. */
export const hasOwnRouteLabel = (route: ActivatedRouteSnapshot): boolean => {
    const ownData: Data = route.routeConfig?.data ?? {};
    const declaresKey = typeof ownData['key'] === 'string' && typeof ownData['module'] === 'string';
    return declaresKey || route.routeConfig?.resolve?.['breadcrumb'] != null;
};

export const getLastChild = (route: ActivatedRoute): ActivatedRoute => {
    while (route.firstChild) {
        route = route.firstChild;
    }
    return route;
};

export const getMergedParams = (route: ActivatedRouteSnapshot): Map<string, string> => {
    const mergeParamMaps = (paramMap: ParamMap): void => {
        paramMap.keys.forEach(key => {
            mergedParams.set(key, paramMap.get(key) ?? '');
        });
    };

    const mergedParams = new Map<string, string>();
    mergeParamMaps(route.paramMap);
    while (route.firstChild) {
        route = route.firstChild;
        mergeParamMaps(route.paramMap);
    }

    return mergedParams;
};

const featureFlagSchema = z.union([
    z.string(),
    z.object({id: z.string(), hideWhenEnabled: z.boolean().optional()}),
]);

export const routeVisibilitySchema = z.object({
    requireBuildingService: z.number().optional(),
    hide: z.boolean().optional(),
    featureFlag: featureFlagSchema.optional(),
});

export const meetsBuildingServiceRequirement = (
    buildingServices: ReadonlyArray<number>,
    requiredService: BuildingServices | undefined,
): boolean => requiredService == null || buildingServices.includes(requiredService);

export const visibleChildRoutes = (
    children: Route[],
    buildingServices: number[],
    featureFlagService: FeatureFlagService,
): Observable<Route[]> => {
    const candidates = children
        .filter(route => route.path != null && route.path !== '')
        .map(route => ({route, data: routeVisibilitySchema.parse(route.data ?? {})}))
        .filter(({data}) => data.hide !== true)
        .filter(({data}) =>
            meetsBuildingServiceRequirement(buildingServices, data.requireBuildingService),
        );

    if (candidates.length === 0) {
        return of([]);
    }

    return combineLatest(
        candidates.map(({route, data}) =>
            featureFlagService.isFeatureFlagEnabled$(data.featureFlag).pipe(
                catchError(() => of(false)),
                map(enabled => ({route, enabled})),
            ),
        ),
    ).pipe(map(results => results.filter(({enabled}) => enabled).map(({route}) => route)));
};

/**
 * The user state once the load has settled, fetching it first when nothing has. Route guards run
 * before the app shell on a direct navigation, so a guard that only selected would wait on
 * 'pending' for ever. Call from an injection context.
 */
export const loadedUserState = (): Observable<UserStateSuccess | StateError> => {
    const store = inject(Store);
    return store.select(selectUser).pipe(
        tap(state => {
            if (state.status === 'pending') {
                store.dispatch(loadUser());
            }
        }),
        filter(
            (state): state is UserStateSuccess | StateError =>
                state.status === 'success' || state.status === 'error',
        ),
        take(1),
    );
};

export const loadedUserHoldsBuildingService = (
    requiredService: BuildingServices | undefined,
): Observable<boolean> =>
    loadedUserState().pipe(
        map(
            state =>
                state.status === 'success' &&
                meetsBuildingServiceRequirement(state.user.buildingServices, requiredService),
        ),
    );
